import React from 'react';
import { SaleChange, SaleChangeStatusEnum } from '@src/core/buyerApiClient';
import useTranslate, { Translate } from '@src/i18n/useTranslate';
import FeedbackMessage from '@src/components/ui/FeedbackMessage';
import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { colors, LayoutColors } from '@src/core/theme';
import { formatScheduleDate } from '@src/domains/scheduling';
import { FeedbackContainer, FeedbackContent } from './styles';
import { MdCheckCircleOutline } from 'react-icons/md';

type RescheduleSaleChangeStatusProps = {
  saleChange: SaleChange;
  i18n: Translate['i18n'];
  onDismiss?: () => void;
};

const statusConfig = (
  saleChange: SaleChange,
  i18n: Translate['i18n'],
  locale: Translate['locale'],
  onDismiss?: () => void,
): Record<string, { type: 'success' | 'info' | 'warning' | 'danger'; message: React.ReactNode } | undefined> => ({
  [SaleChangeStatusEnum.Applied]: {
    type: 'success',
    message: (
      <FeedbackContainer>
        <FeedbackContent>
          <MdCheckCircleOutline size={20} color={LayoutColors.successDark} />
          <span>{i18n('reschedule_applied_message')}</span>
        </FeedbackContent>
        <IconX size={20} id='close-icon' onClick={onDismiss} role='button' aria-label='Close' />
      </FeedbackContainer>
    ),
  },
  [SaleChangeStatusEnum.Expired]: {
    type: 'danger',
    message: (
      <FeedbackContainer>
        <FeedbackContent>
          <IconAlertTriangle size={20} color={LayoutColors.errorDark} />
          <span>{i18n('reschedule_expired_message')}</span>
        </FeedbackContent>
      </FeedbackContainer>
    ),
  },
  [SaleChangeStatusEnum.Pending]: {
    type: 'warning',
    message: (
      <FeedbackContainer style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
        <FeedbackContent>
          <IconAlertTriangle size={20} color={colors.yellow[500]} />
          <span style={{ fontWeight: 600, color: colors.yellow[500] }}>
            {i18n('reschedule_pending_payment_message_title')}
          </span>
        </FeedbackContent>
        <span style={{ color: colors.yellow[500] }}>
          {i18n('reschedule_pending_payment_message', {
            date: formatScheduleDate(saleChange.saleItem?.scheduleDate ?? '', locale),
          })}
        </span>
      </FeedbackContainer>
    ),
  },
});

const RescheduleSaleChangeStatus = (props: RescheduleSaleChangeStatusProps) => {
  const { saleChange, i18n, onDismiss } = props;
  const { locale } = useTranslate();

  const config = statusConfig(saleChange, i18n, locale, onDismiss)[saleChange.status];

  if (!config) return null;

  return <FeedbackMessage type={config.type} message={config.message} />;
};

export default RescheduleSaleChangeStatus;
